import { useEffect } from "react";
import { motion } from "framer-motion";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { X } from "lucide-react";
import { useDismissible } from "@/hooks/use-dismissible";
import { BANNER_VERSIONS } from "@/lib/banner-versions";

const pillars = [
  {
    heading: "100% Verified Profiles",
    body: "Every profile is hand-verified as a Riot Games alum before it appears here. If you can see it, a person checked it.",
  },
  {
    heading: "Current Availability",
    body: "Open to Work badges expire automatically after 30 days. If you can see one, that member switched it on recently and means it.",
  },
  {
    heading: "No Account Required",
    body: "Browse the directory freely. When you find the right person, their LinkedIn and portfolio are one click away.",
  },
];

interface RecruiterValuePropsProps {
  onDismissed?: () => void;
}

export default function RecruiterValueProps({ onDismissed }: RecruiterValuePropsProps) {
  const isReduced = useReducedMotion();
  const { visible, dismissed, dismiss, onAnimationComplete } =
    useDismissible(BANNER_VERSIONS.recruiter.currentKey);

  useEffect(() => {
    if (dismissed) onDismissed?.();
  }, [dismissed, onDismissed]);

  if (dismissed) return null;

  return (
    <motion.section
      initial={isReduced ? false : { opacity: 0, y: 12 }}
      animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: isReduced ? 0 : -12 }}
      transition={{ duration: isReduced ? 0 : 0.35, ease: "easeInOut" }}
      className="w-full px-4"
    >
      <div className="container mx-auto max-w-6xl relative">
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full border border-primary/70 flex items-center justify-center text-primary/80 hover:border-primary hover:text-primary hover:bg-primary/10 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <motion.div
          animate={
            visible
              ? { height: "auto", paddingBottom: 40 }
              : { height: 0, paddingBottom: 0 }
          }
          transition={{ duration: isReduced ? 0 : 0.35, ease: "easeInOut" }}
          onAnimationComplete={onAnimationComplete}
          style={{ overflow: "hidden" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {pillars.map((pillar) => (
              <div
                key={pillar.heading}
                className="bg-zinc-900/60 border border-white/[0.08] rounded-xl p-6 flex flex-col gap-3"
              >
                <div className="w-8 h-0.5 bg-primary rounded-full" />
                <h3 className="font-bold text-white text-base uppercase tracking-tight leading-snug" style={{ fontFamily: "var(--font-space-grotesk, 'Space Grotesk', sans-serif)" }}>
                  {pillar.heading}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed font-sans">{pillar.body}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
}
